import { ApolloClient, createHttpLink, InMemoryCache, NormalizedCacheObject, split, ApolloLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { LocalStorageWrapper, persistCache } from 'apollo3-cache-persist';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { AppConfig } from './config';
import { MessageService } from './messageService';
import { Message } from './types/message';
import { Logger } from './utils/logger';
import { TokenRefreshService } from './services/tokenRefreshService';

const logger = new Logger('ApolloClient');

const APOLLO_CACHE_KEY_PREFIX = 'apollo-cache-persist';

const purgeStaleApolloCacheKeys = (currentKey: string): void => {
  try {
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith(APOLLO_CACHE_KEY_PREFIX) && key !== currentKey)
      .forEach((key) => window.localStorage.removeItem(key));
  } catch (error) {
    logger.debug('Failed to purge stale Apollo cache keys', error);
  }
};

const createApolloClient = async (): Promise<ApolloClient<NormalizedCacheObject>> => {
  logger.info('Creating Apollo Client');

  // Create HTTP link for queries and mutations
  const httpLink = createHttpLink({
    uri: AppConfig.graphqlApiRoot
  });

  // Create WebSocket link for subscriptions
  const wsLink = new GraphQLWsLink(
    createClient({
      url: AppConfig.graphqlWs,
      connectionParams: () => {
        const accessToken = localStorage.getItem(AppConfig.tokenKey);
        return accessToken ? { authorization: `Bearer ${accessToken}` } : {};
      }
    })
  );

  // Split traffic between HTTP and WebSocket based on operation type
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    httpLink
  );

  // Add authentication headers to requests
  const authLink = setContext((_, { headers }) => {
    const accessToken = localStorage.getItem(AppConfig.tokenKey);
    return {
      headers: {
        ...headers,
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {})
      }
    };
  });

  const tokenRefreshLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((response) => {
      const context = operation.getContext();
      const headers = context.response?.headers;
      const newToken = headers?.get?.('X-Access-Token');
      if (newToken) {
        TokenRefreshService.updateToken(newToken);
      }
      return response;
    });
  });

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, extensions }) => {
        const code = extensions?.code as string;
        if (code === 'FORBIDDEN') {
          const required = (extensions as any)?.requiredRights;
          const have = (extensions as any)?.userRights;
          const resolver = (extensions as any)?.resolver;
          const handler = (extensions as any)?.handler;
          logger.warn(
            `GraphQL FORBIDDEN in ${operation.operationName} | ${resolver}.${handler} | required=[${(required || []).join(',')}] | userHas=[${(have || []).join(',')}]`
          );
        } else {
          logger.warn(`GraphQL error [${code}] in ${operation.operationName}: ${message}`);
        }

        switch (code) {
          case 'UNAUTHENTICATED':
            if (localStorage.getItem(AppConfig.tokenKey)) {
              localStorage.removeItem(AppConfig.tokenKey);
              localStorage.removeItem(AppConfig.adminTokenKey);
              MessageService.sendMessage(Message.error('Authentication required. Please log in.'));
              if (!window.location.pathname.startsWith('/login')) {
                window.location.href = '/login';
              }
            }
            break;
          case 'FORBIDDEN':
            MessageService.sendMessage(Message.error('You do not have permission to perform this action'));
            break;
          case 'INTERNAL_SERVER_ERROR':
            MessageService.sendMessage(Message.error('An unexpected error occurred. Please try again.'));
            break;
          default:
            MessageService.sendMessage(Message.error(message || 'An error occurred'));
        }
      });
    }

    if (networkError) {
      logger.error(`Network error: ${networkError.message}`);
      MessageService.sendMessage(Message.error('Network error. Please check your connection.'));
    }
  });

  // Set up cache with proper ID generation
  const cache = new InMemoryCache({
    dataIdFromObject: (object: any) => {
      if (object.__typename && object.id) {
        return `${object.__typename}:${object.id}`;
      }
      return undefined; // Let Apollo Client generate a unique ID
    }
  });

  const cacheKey = `${APOLLO_CACHE_KEY_PREFIX}-${__APP_VERSION__}`;
  purgeStaleApolloCacheKeys(cacheKey);

  try {
    await persistCache({
      cache,
      storage: new LocalStorageWrapper(window.localStorage),
      key: cacheKey,
      maxSize: 1024 * 1024 * 2,
      debug: process.env.NODE_ENV !== 'production'
    });
    logger.debug('Apollo cache persisted successfully');
  } catch (error: any) {
    if (error?.name === 'QuotaExceededError') {
      logger.warn('Apollo cache persist failed: LocalStorage quota exceeded. Falling back to in-memory cache only.');
    } else {
      logger.debug('Error persisting Apollo cache', error);
    }
    // Do nothing else to avoid blocking the app or causing data loss
  }

  // Create the Apollo Client
  const client = new ApolloClient({
    cache,
    link: ApolloLink.from([errorLink, authLink, tokenRefreshLink, splitLink]),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all'
      },
      query: {
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      },
      mutate: {
        errorPolicy: 'all'
      }
    },
    name: 'grid-funding-client',
    version: __APP_VERSION__
  });

  logger.info('Apollo Client created successfully');
  return client;
};

export default createApolloClient;